import { useEffect, useRef, useState } from "react";
import { useEventClock, findLive } from "@/lib/scheduleNow";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Clock, MapPin, Sparkles } from "lucide-react";

const DAYS = [
  { key: "2026-10-30", label: "Friday · Oct 30" },
  { key: "2026-10-31", label: "Saturday · Oct 31" },
];

export default function Schedule() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("2026-10-30");
  const clock = useEventClock();
  const live = findLive(items, clock);
  const liveRef = useRef(null);
  const jumped = useRef(false);

  useEffect(() => {
    api.get("/schedule").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (live && !jumped.current) {
      jumped.current = true;
      setActive(live.date);
      setTimeout(() => liveRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    }
  }, [live]);

  const dayItems = items.filter((i) => i.date === active);

  return (
    <AppLayout>
      <PageHeader subtitle="Two Days of Competition" title="Schedule" />
      <p data-testid="schedule-tentative" className="mx-4 mt-1 rounded-lg bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
        Schedule is tentative and subject to change.
      </p>
      <div className="sticky top-0 z-30 bg-background/90 px-4 py-3 backdrop-blur">
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted p-1">
          {DAYS.map((d) => (
            <button key={d.key} data-testid={`schedule-tab-${d.key}`} onClick={() => setActive(d.key)}
              className={`rounded-lg py-2.5 text-sm font-semibold transition-colors ${active === d.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 px-4 pt-2">
        {dayItems.length === 0 && <p className="text-sm text-muted-foreground">No events scheduled.</p>}
        {dayItems.map((item, i) => {
          const isLive = live?.id === item.id;
          return (
          <div key={item.id} ref={isLive ? liveRef : null} data-testid={`schedule-item-${item.id}`} data-live={isLive ? "true" : undefined}
            className={`animate-fade-up rounded-xl border bg-card p-4 shadow-sm transition-shadow ${isLive ? "border-green-500 ring-2 ring-green-500/40 shadow-md" : "border-border"}`}
            style={{ animationDelay: `${i * 40}ms` }}>
            {isLive && (
              <p data-testid="schedule-now-happening" className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Now Happening
              </p>
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-1.5 font-display text-lg font-bold text-secondary">
                <Clock size={16} /> {item.start_time}
              </div>
              {!isLive && <StatusBadge status={item.status} testid={`status-${item.id}`} />}
            </div>
            <h3 className="mt-1 font-display text-xl font-bold text-primary">{item.name}</h3>
            {item.division && (
              <span data-testid={`schedule-division-${item.id}`} className="mt-1 inline-block rounded-full bg-secondary/15 px-2.5 py-0.5 text-xs font-bold text-secondary">
                {item.division}
              </span>
            )}
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin size={14} /> {item.arena}
            </p>
            {item.description && <p className="mt-2 text-sm text-foreground/80">{item.description}</p>}
            {item.sponsor && (
              <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-secondary">
                <Sparkles size={13} /> Sponsored by {item.sponsor}
              </p>
            )}
          </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
